/* eslint-disable */
import { getLocale, experimentalStaticLocale } from '../runtime.js';

/** @typedef {import('../runtime.js').LocalizedString} LocalizedString */

/** @typedef {{ operator: NonNullable<unknown>, email: NonNullable<unknown> }} Legal_Health_BodyInputs */

const en_legal_health_body = /** @type {(inputs: Legal_Health_BodyInputs) => LocalizedString} */ (i) => {
	return /** @type {LocalizedString} */ (`This policy applies to consumers in Washington State and Nevada, under Washington's My Health My Data Act and Nevada's SB 370. It describes consumer health data that ${i?.operator} collects through Complete Athlete. It adds to our general [Privacy Policy](/privacy).

## 1. Consumer health data we collect

- Injuries, health notes, supplements and allergens from questionnaires.
- Sex, birth year, height, weight, weigh-ins, body measurements, progress photos and your own metrics.
- Workouts, cardio and muscle recovery.
- Meal logs and meal photos, and diet preferences.
- Health-related services you search for or book, such as pregnancy or rehabilitation training, and your intake answers.
- Your approximate location, to the extent it reveals an attempt to get a health-related service.

## 2. Why we collect and use it

To provide the features you ask for: logging, training and nutrition plans, recovery estimates, AI meal reading and food swaps, booking, and sharing with businesses you choose. We also use it to keep the service secure and to meet legal duties.

## 3. Where it comes from

From you, from your device, from businesses you are a client of (such as notes and plans), and from assistants you connect.

## 4. What we share

We share consumer health data with:

- **Businesses you are a client of**, as you direct. Your recovery estimate is shared by default once you are a client, and you can turn it off. Other shares are off until you turn them on.
- **Assistants you connect** with an API key.

**Service providers (processors) that handle it for us:** Railway (hosting and storage), Anthropic (AI features), Stripe (payments, where a purchase reveals it), Resend (email), SMSAPI (text messages) and Sentry (error reports).

**Third parties:** the businesses you choose are listed to you in the app under Settings, Sharing. **Affiliates:** we have none that receive this data. We do not sell consumer health data.

## 5. Your rights

You can:

- confirm whether we collect, share or sell your consumer health data, and access it;
- get a list of the third parties and affiliates we shared it with;
- withdraw consent;
- have it deleted, including from our service providers and from third parties we shared it with.

You can withdraw consent in your settings, under Health data, and we then delete this data as our [Privacy Policy](/privacy) describes. For anything else, write to ${i?.email}. We respond within 45 days. If we refuse, you can appeal by replying to our answer, and we will answer your appeal within 45 days. If your appeal is denied, you can complain to the Washington State Attorney General or the Nevada Attorney General. We will not treat you differently for using these rights.`)
};

const pl_legal_health_body = /** @type {(inputs: Legal_Health_BodyInputs) => LocalizedString} */ (i) => {
	return /** @type {LocalizedString} */ (`Niniejsza polityka dotyczy konsumentów w stanach Waszyngton i Nevada, na podstawie obowiązującego w stanie Waszyngton My Health My Data Act oraz obowiązującej w stanie Nevada ustawy SB 370. Opisuje dane o zdrowiu konsumentów, które ${i?.operator} zbiera za pośrednictwem Complete Athlete. Uzupełnia naszą ogólną [Politykę prywatności](/privacy).

## 1. Dane o zdrowiu konsumentów, które zbieramy

- Kontuzje, uwagi o zdrowiu, suplementy i alergeny z ankiet.
- Płeć, rok urodzenia, wzrost, waga, ważenia, pomiary ciała, zdjęcia sylwetki i Twoje własne parametry.
- Treningi, cardio i regeneracja mięśni.
- Dziennik posiłków i zdjęcia posiłków oraz preferencje dietetyczne.
- Usługi związane ze zdrowiem, które wyszukujesz lub rezerwujesz, na przykład treningi dla kobiet w ciąży lub rehabilitacyjne, oraz Twoje odpowiedzi z ankiety wstępnej.
- Twoja przybliżona lokalizacja, w zakresie, w jakim ujawnia próbę skorzystania z usługi związanej ze zdrowiem.

## 2. W jakim celu je zbieramy i wykorzystujemy

Aby zapewnić funkcje, o które prosisz: zapisywanie danych, plany treningowe i żywieniowe, szacunki regeneracji, odczytywanie posiłków i proponowanie zamienników produktów przez AI, rezerwacje oraz udostępnianie wybranym przez Ciebie firmom. Wykorzystujemy je też, aby zapewnić bezpieczeństwo usługi i spełniać obowiązki prawne.

## 3. Skąd pochodzą

Od Ciebie, z Twojego urządzenia, od firm, których jesteś klientem (na przykład notatki i plany), oraz od połączonych przez Ciebie asystentów.

## 4. Komu je udostępniamy

Dane o zdrowiu konsumentów udostępniamy:

- **Firmom, których jesteś klientem**, zgodnie z Twoimi poleceniami. Twój szacunek regeneracji jest udostępniany domyślnie, gdy jesteś klientem firmy, i możesz to wyłączyć. Pozostałe udostępnienia są wyłączone, dopóki ich nie włączysz.
- **Asystentom, których łączysz** za pomocą klucza API.

**Dostawcy usług (podmioty przetwarzające), którzy obsługują je dla nas:** Railway (hosting i przechowywanie), Anthropic (funkcje AI), Stripe (płatności, jeśli ujawnia je zakup), Resend (e-mail), SMSAPI (SMS-y) i Sentry (raporty o błędach).

**Podmioty trzecie:** wybrane przez Ciebie firmy są wymienione w aplikacji w sekcji Ustawienia, Udostępnianie. **Podmioty powiązane:** nie mamy takich, które otrzymywałyby te dane. Nie sprzedajemy danych o zdrowiu konsumentów.

## 5. Twoje prawa

Możesz:

- uzyskać potwierdzenie, czy zbieramy, udostępniamy lub sprzedajemy Twoje dane o zdrowiu konsumentów, oraz uzyskać do nich dostęp;
- otrzymać listę podmiotów trzecich i podmiotów powiązanych, którym je udostępniliśmy;
- wycofać zgodę;
- żądać ich usunięcia, także u naszych dostawców usług i u podmiotów trzecich, którym je udostępniliśmy.

Zgodę możesz wycofać w ustawieniach, w sekcji Dane dotyczące zdrowia, a my usuniemy wtedy te dane w sposób opisany w naszej [Polityce prywatności](/privacy). W pozostałych sprawach napisz na adres ${i?.email}. Odpowiadamy w ciągu 45 dni. Jeśli odmówimy, możesz odwołać się, odpowiadając na naszą wiadomość, a my odpowiemy na odwołanie w ciągu 45 dni. Jeśli Twoje odwołanie zostanie odrzucone, możesz złożyć skargę do Prokuratora Generalnego stanu Waszyngton (Washington State Attorney General) lub Prokuratora Generalnego stanu Nevada (Nevada Attorney General). Nie będziemy traktować Cię inaczej z powodu korzystania z tych praw.`)
};

/**
* | output |
* | --- |
* | "This policy applies to consumers in Washington State and Nevada, under Washington's My Health My Data Act and Nevada's SB 370. It describes consumer health d..." |
*
* @param {Legal_Health_BodyInputs} inputs
* @param {{ locale?: "en" | "pl" }} options
* @returns {LocalizedString}
*/
export const legal_health_body = /** @type {((inputs: Legal_Health_BodyInputs, options?: { locale?: "en" | "pl" }) => LocalizedString) & import('../runtime.js').MessageMetadata<Legal_Health_BodyInputs, { locale?: "en" | "pl" }, {}>} */ ((inputs, options = {}) => {
	const locale = experimentalStaticLocale ?? options.locale ?? getLocale()
	if (locale === "pl") return pl_legal_health_body(inputs)
	return en_legal_health_body(inputs)
});